<script setup lang="ts">
/**
 * 顶部导航。
 *
 * 「对话」这一项的点击处理是**整个移植里最容易被改坏的地方**：
 * 站在 /chat/<id> 上再点「对话」，要开一个新会话。但路由是同一个组件，
 * 单纯 push('/chat') 不会重挂 `ChatScreen`，也不会重跑 `useChatController`
 * 的初始化——必须**先本地 reset，再改地址**（`requestChatReset()`）。
 * 顺序反了会先看到"地址栏已经变成 /chat，内容还是上一个会话"的一帧。
 *
 * 另外这里不阻止默认导航：地址该变还是要变，只是先让本地状态跟上。
 *
 * 身份（`role`）在这里只做**外壳切换**，不做权限判定：导航项换一套、额度相关的三块
 * （档位徽章 / 额度条 / 到期横幅）对管理员整体不渲染。真正的门禁在 router 守卫与服务端，
 * 这里若也写一遍"谁能看什么"，迟早和那两处对不上。
 */

import { computed, onMounted, onUnmounted, ref } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { roleLabel, type PlanId } from '@spirit/contracts';

import PlanBadge from '../quota/PlanBadge.vue';
import QuotaBar from '../quota/QuotaBar.vue';
import { cn } from '../../lib/cn';
import { isStaffRole, signOut, useAuth } from '../../lib/auth-store';
import { requestChatReset } from '../../lib/chat-reset-registry';
import { mockModeLabel } from '../../lib/env';
import { maskPhone } from '../../lib/format';
import { rateLimit } from '../../lib/rate-limit';
import {
  describeExpiryNotice,
  EXPIRY_TICK_MS,
  expiryStatusOf,
  planBadgeToneOf,
  type PlanBadgeTone,
} from '../../lib/subscription';
import { useSubscription } from '../../lib/subscription-store';

const NAV_ITEMS: ReadonlyArray<{ href: string; label: string }> = [
  { href: '/chat', label: '对话' },
  { href: '/conversations', label: '会话' },
  { href: '/profile', label: '我的' },
  { href: '/quota', label: '额度' },
  // 放在最后：购买是低频动作，但它必须在额度条旁边随手可点——
  // 用户看到"额度快用完"的下一眼就该找到这里。
  { href: '/checkout', label: '购买' },
];

/**
 * 管理员 / ROOT 看到的后台导航。
 *
 * 与 NAV_ITEMS 一样是**静态表**：这里不按 role 逐项过滤（比如"渠道只有 ROOT 能看"），
 * 因为「点进去被弹回概览」比「菜单项忽隐忽现」更好排查——留下的是同一个后台外壳，
 * 而不是每个人记一套不同的菜单。ROOT 专属页面的限制在 router 守卫里集中处理。
 */
const STAFF_NAV_ITEMS: ReadonlyArray<{ href: string; label: string }> = [
  { href: '/admin/overview', label: '概览' },
  { href: '/admin/users', label: '用户' },
  { href: '/admin/usage', label: '用量明细' },
  { href: '/admin/channels', label: '渠道' },
  { href: '/admin/accounts', label: '管理员账户' },
];

const route = useRoute();
const router = useRouter();
const auth = useAuth();
const subscription = useSubscription();

/**
 * 每 60 秒推进一次的"现在"。
 *
 * 剩余时间必须自己走：用户开着页面跨过 72 小时线（或跨过到期时刻）时，不重算就既不会
 * 出现警告、也不会在过期后把档位收回 Free。这类"页面放着不动就显示错"的缺陷最难被
 * 发现——因为刷新一下就好了。
 */
const now = ref(Date.now());
let ticker: number | null = null;

onMounted(() => {
  ticker = window.setInterval(() => {
    now.value = Date.now();
  }, EXPIRY_TICK_MS);
});

onUnmounted(() => {
  if (ticker !== null) {
    window.clearInterval(ticker);
    ticker = null;
  }
});

/**
 * 档位取自**有效**额度快照（服务端已按到期时间判过），订阅只作为额度还没到时的兜底。
 * 过期后 `quota.planId` 就是 free，而 `subscription.planId` 还是买过的那一档——
 * 两者不一致时以额度为准，否则徽章会在到期后继续显示 Lite。
 */
const badgePlanId = computed<PlanId | null>(
  () => auth.quota?.planId ?? subscription.subscription?.planId ?? null,
);

const expiryStatus = computed(() => expiryStatusOf(subscription.subscription, now.value));
const badgeTone = computed<PlanBadgeTone | null>(() =>
  badgePlanId.value === null ? null : planBadgeToneOf(badgePlanId.value, expiryStatus.value),
);
const expiryNotice = computed(() =>
  describeExpiryNotice(subscription.subscription, expiryStatus.value),
);

const mockLabel = mockModeLabel();
const path = computed(() => route.path);

/**
 * 是否后台身份。用 `isStaffRole(auth.role)` 而不是 `auth.role !== 'user'`：
 * 后者在 role 还没恢复（`null`）时会把访客当成管理员。
 */
const isStaff = computed(() => isStaffRole(auth.role));

const navItems = computed(() => (isStaff.value ? STAFF_NAV_ITEMS : NAV_ITEMS));

/** 后台外壳里显示的角色徽章文案，取自契约的 ROLE_LABEL，不在前端另写一套。 */
const staffRoleLabel = computed(() =>
  auth.role === null ? null : roleLabel(auth.role),
);

/** 直接从响应式单例读，倒计时才会每秒推进——`useRateLimit()` 只是一次快照。 */
const rateLimitActive = computed(() => rateLimit.snapshot.active);
const rateLimitSeconds = computed(() => rateLimit.snapshot.secondsRemaining);

function isActive(href: string): boolean {
  return path.value === href || path.value.startsWith(`${href}/`);
}

async function onSignOut(): Promise<void> {
  await signOut();
  await router.push('/');
}

function onNavigate(href: string): void {
  if (href !== '/chat' || !path.value.startsWith('/chat/')) return;
  requestChatReset();
  void router.replace(href);
}

const identity = computed(() => {
  const user = auth.user;
  if (user === null) return null;
  return user.phone === null ? user.nickname : maskPhone(user.phone);
});
</script>

<template>
  <header class="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
    <div class="mx-auto flex max-w-5xl items-center gap-3 px-4 py-2.5">
      <RouterLink :to="isStaff ? '/admin/overview' : '/'" class="flex shrink-0 items-baseline gap-2">
        <span class="text-base font-semibold text-brand-700">智家教育</span>
        <span class="hidden text-xs text-slate-500 sm:inline">
          {{ isStaff ? '管理后台' : '文化服务平台' }}
        </span>
      </RouterLink>

      <nav class="no-scrollbar -mx-1 flex flex-1 items-center gap-1 overflow-x-auto px-1">
        <RouterLink
          v-for="item in navItems"
          :key="item.href"
          :to="item.href"
          :aria-current="isActive(item.href) ? 'page' : undefined"
          :class="
            cn(
              'shrink-0 rounded-lg px-3 py-1.5 text-sm transition-colors',
              isActive(item.href)
                ? 'bg-brand-50 font-medium text-brand-700'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
            )
          "
          @click="onNavigate(item.href)"
        >
          {{ item.label }}
        </RouterLink>
      </nav>

      <div class="flex shrink-0 items-center gap-2">
        <span
          class="hidden rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 md:inline"
          title="当前使用本地 Mock 数据，所有请求都不会发到后端"
        >
          {{ mockLabel }}
        </span>

        <!-- 档位徽章：右上角是最容易被扫到的地方，且它自己就是续费/升级入口。
             管理员没有个人套餐（quota 恒为 null），徽章留在这里只会让他们以为后台在卖东西。 -->
        <PlanBadge
          v-if="!isStaff && auth.isAuthenticated && badgePlanId !== null && badgeTone !== null"
          :plan-id="badgePlanId"
          :tone="badgeTone"
          :status="expiryStatus"
        />

        <!-- 后台身份标识：管理员和 ROOT 的界面差别只在少数页面，标出当前身份能省掉一场"我是不是点错了"的排查。 -->
        <span
          v-if="isStaff && staffRoleLabel !== null"
          class="rounded bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700"
        >
          {{ staffRoleLabel }}
        </span>

        <!-- 登录态还没从 localStorage 恢复完：渲染"未登录"会先闪一下登录按钮。 -->
        <span
          v-if="!auth.hydrated"
          class="h-8 w-16 animate-pulse rounded-lg bg-slate-100"
          aria-hidden="true"
        />

        <template v-else-if="auth.isAuthenticated && auth.user !== null">
          <span class="hidden max-w-[10rem] truncate text-xs text-slate-500 sm:inline">
            {{ identity }}
          </span>
          <button
            type="button"
            class="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            @click="void onSignOut()"
          >
            {{ isStaff ? '退出登录' : '退出' }}
          </button>
        </template>

        <RouterLink
          v-else
          to="/login"
          class="rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          登录
        </RouterLink>
      </div>
    </div>

    <div
      v-if="rateLimitActive"
      class="bg-amber-50 px-4 py-1.5 text-center text-xs text-amber-900"
      role="status"
      aria-live="polite"
    >
      操作过于频繁，请在
      <span class="font-medium tabular-nums">{{ rateLimitSeconds }}</span>
      秒后重试
    </div>

    <!-- 到期警示必须比"额度快用完"更早出现：额度用完只是当天难受，套餐到期是直接降档。
         管理员没有订阅，这条对他们永远无意义——`subscription` 可能是上一个身份残留的快照。 -->
    <div
      v-if="!isStaff && expiryNotice !== null"
      class="border-t border-amber-200 bg-amber-100/70 px-4 py-1.5 text-center text-xs text-amber-900"
      role="status"
      aria-live="polite"
    >
      {{ expiryNotice }}
      <RouterLink
        to="/checkout"
        class="ml-1 font-medium underline decoration-amber-400 underline-offset-2 hover:text-amber-950"
      >
        立即续费
      </RouterLink>
    </div>

    <QuotaBar v-if="!isStaff" :quota="auth.quota" compact>
      <template #trailing>
        <PlanBadge
          v-if="badgePlanId !== null && badgeTone !== null"
          :plan-id="badgePlanId"
          :tone="badgeTone"
          :status="expiryStatus"
        />
      </template>
    </QuotaBar>
  </header>
</template>
